import {
  CurrentPayPlan,
  RoleType,
  MembershipLevel,
  MSSMemberStatus,
  MembershipProductType,
  CartProductCode,
  GenderType,
  PhoneType,
} from '@ace-clublabs/common-membership-api-types';
import { IState, IMemberDetails } from './model';
import {
  CallbackPaymentMethod,
  GenderCode,
  MSSMobileAuthStatus,
  MSSNotificationType,
  MSSProductStatus,
} from '@lib/types';

export const membershipDetailsStateStub: IMemberDetails = {
  sessionId: 'some-session-id',
  clubCode: '004',
  loggedInCustomerId: '12345678',
  clubRegion: undefined,
  termEffectiveDate: '06/24/2024',
  termExpirationDate: '06/25/2025',
  priorTermExpirationDate: '06/25/2025',
  membershipNumber: '6200041234567801',
  currentBillPlan: CurrentPayPlan.RECURRINGCREDITORDEBITCARD,
  isAutopayRequired: false,
  joinYear: '2015',
  inRenewal: true,
  inArrears: false,
  inGrace: false,
  campaignCode: '000007',
  members: [
    {
      customerId: '12345678',
      firstName: 'Janet',
      middleName: 'A',
      lastName: 'Morales',
      nameSuffix: 'Sr',
      roleType: RoleType.PRIMARY,
      status: MSSMemberStatus.EXISTING,
      rules: {
        viewMembership: true,
        changeMembership: true,
        makePayment: true,
        updateEmail: true,
        updatePhone: true,
        nameChange: {
          firstName: true,
          middleName: true,
          lastName: true,
          suffix: true,
        },
      },
      residenceAddress: {
        addressLine1: '123 Main St',
        addressLine2: 'Apartment 1',
        city: 'Los Angeles',
        state: 'CA',
        zip: '90007',
      },
      mailingAddress: {
        addressLine1: '456 Main St',
        city: 'Los Angeles',
        state: 'CA',
        zip: '90007',
      },
    },
    {
      customerId: '11111111',
      firstName: 'James',
      lastName: 'Morales',
      emailAddress: 'jmorales@email.com',
      gender: GenderType.MALE,
      dateOfBirth: '01/01/1999',
      roleType: RoleType.ADULT_ASSOCIATE,
      status: MSSMemberStatus.EXISTING,
      rules: {
        viewMembership: true,
        changeMembership: true,
        makePayment: true,
        updateEmail: true,
        updatePhone: true,
        nameChange: {
          firstName: true,
          middleName: true,
          lastName: true,
          suffix: true,
        },
      },
      residenceAddress: {
        addressLine1: '123 Main St',
        addressLine2: 'Apartment 1',
        city: 'Los Angeles',
        state: 'CA',
        zip: '90007',
      },
      mailingAddress: {
        addressLine1: '456 Main St',
        city: 'Los Angeles',
        state: 'CA',
        zip: '90007',
      },
    },
    {
      customerId: '22222222',
      firstName: 'Joe',
      lastName: 'Morales',
      middleName: 'A',
      nameSuffix: 'Jr.', //confirm what format suffix is returned from api
      roleType: RoleType.DEPENDENT,
      status: MSSMemberStatus.EXISTING,
      dateOfBirth: '08/01/2005', //need to confirm if being returned from api
      gender: GenderType.MALE, //need to confirm if being returned from api
      rules: {
        viewMembership: false,
        changeMembership: false,
        makePayment: false,
        updateEmail: true,
        updatePhone: true,
        nameChange: {
          firstName: true,
          middleName: true,
          lastName: true,
          suffix: true,
        },
      },
      residenceAddress: {
        addressLine1: '123 Main St',
        addressLine2: 'Apartment 1',
        city: 'Los Angeles',
        state: 'CA',
        zip: '90007',
      },
      mailingAddress: {
        addressLine1: '456 Main St',
        city: 'Los Angeles',
        state: 'CA',
        zip: '90007',
      },
    },
  ],
  loggedInMember: {
    emailAddress: 'jmorales@email.com',
    customerId: '12345678',
    firstName: 'Janet',
    lastName: 'Morales',
    middleName: 'Smith',
    nameSuffix: 'Jr.',
    gender: GenderType.FEMALE,
    roleType: RoleType.PRIMARY,
    status: MSSMemberStatus.EXISTING,
    mobilePhoneNumber: '555-555-5555',
    phoneNumbers: [
      {
        type: PhoneType.MOBILE,
        areaCode: '555',
        prefix: '555',
        suffix: '5555',
        mobileAuthStatus: MSSMobileAuthStatus.CONSENTED,
      },
      {
        type: PhoneType.HOME,
        areaCode: '666',
        prefix: '666',
        suffix: '6666',
        mobileAuthStatus: MSSMobileAuthStatus.NOT_RESPONDED,
        isMobilePhone: true,
      },
    ],
    residenceAddress: {
      addressLine1: '123 Main St',
      addressLine2: 'Apartment 1',
      city: 'Los Angeles',
      state: 'CA',
      zip: '90007',
    },
    mailingAddress: {
      addressLine1: '456 Main St',
      city: 'Los Angeles',
      state: 'CA',
      zip: '90007',
    },
    rules: {
      viewMembership: true,
      changeMembership: true,
      makePayment: true,
      updateEmail: true,
      updatePhone: true,
      nameChange: {
        firstName: true,
        middleName: true,
        lastName: true,
        suffix: true,
      },
    },
  },
  benefitLevels: {
    existingLevel: MembershipLevel.CLASSIC,
    pendingLevel: MembershipLevel.CLASSIC,
    effectiveDate: '6/24/2024',
  },
  products: [
    {
      type: MembershipProductType.RV,
      status: MSSProductStatus.EXISTING,
      effectiveDate: '06-30-2024',
    },
  ],
  enrollments: [],
  hasPaymentInTransit: false,
  paymentInTransitDetails: [
    //uncomment when hasPaymentInTransit is true
    // {
    //   pitDate: '2024-03-21',
    //   pitAmount: '158',
    // },
  ],
  pendingProducts: [],
  prospectiveMemberRules: {
    allowedBillPlans: [],
    allowedGenders: [GenderCode.F, GenderCode.M, GenderCode.X],
    allowedMembershipLevels: [MembershipLevel.CLASSIC, MembershipLevel.PLUS, MembershipLevel.PREMIER],
    autopayDiscountAllowed: false,
    downgradesAllowed: true,
    memberProducts: [
      {
        productType: MembershipProductType.RV,
        productRules: [
          {
            membershipLevel: MembershipLevel.CLASSIC,
            rule: MSSProductStatus.ALLOWED,
          },
          {
            membershipLevel: MembershipLevel.PLUS,
            rule: MSSProductStatus.INCLUDED,
          },
          {
            membershipLevel: MembershipLevel.PREMIER,
            rule: MSSProductStatus.INCLUDED,
          },
        ],
      },
    ],
    memberRoles: [
      {
        roleType: RoleType.PRIMARY,
        maximumAge: 140,
        minimumAge: 0,
        maximumAllowed: 1,
        isBirthDateRequired: false,
      },
      {
        roleType: RoleType.ADULT_ASSOCIATE,
        maximumAge: 140,
        minimumAge: 0,
        maximumAllowed: 1,
        isBirthDateRequired: false,
      },
      {
        roleType: RoleType.DEPENDENT,
        maximumAge: 20,
        minimumAge: 0,
        maximumAllowed: 2,
        isBirthDateRequired: true,
      },
    ],
    upgradesAllowed: true,
  },
  memberCosts: {
    priceCart: {
      items: [
        {
          id: 'CartItem-ServiceLevel',
          code: CartProductCode.SERVICELEVEL,
          description: 'Classic Membership',
          price: 57,
          discount: 0,
          discountedPrice: 57,
        },
        {
          id: 'CartItem-Associate',
          code: CartProductCode.ASSOCIATE,
          description: 'Associate Member',
          price: 35,
          discount: 35,
          discountedPrice: 0,
        },
        {
          id: 'CartItem-RenewalDiscounts',
          code: CartProductCode.BILLPLANDISCOUNT,
          description: 'Renewal Discounts',
          price: 0,
          discount: 28.5,
          discountedPrice: -28.5,
        },
        {
          id: 'CartItem-AutopayDiscount',
          code: CartProductCode.AUTOPAY_DISCOUNT,
          description: 'One-time Auto Pay Discount',
          price: 0,
          discount: 4.5,
          discountedPrice: -4.5,
        },
      ],
      salesTax: 5,
      savings: 20,
      total: 44,
    },
    prospectiveCosts: {
      classic: {
        memberRoles: {
          DE: 37,
        },
        products: {
          RV: 42,
        },
        discounts: {
          autopay: 4.5,
        },
      },
      plus: {
        memberRoles: {
          DE: 37,
        },
        products: {
          RV: 42,
        },
        discounts: {
          autopay: 4.5,
        },
      },
      premier: {
        memberRoles: {
          DE: 37,
        },
        products: {
          RV: 42,
        },
        discounts: {
          autopay: 4.5,
        },
      },
    },
    additionalDiscounts: [],
  },
};

export const mockState: IState = {
  memberDetails: membershipDetailsStateStub,
  paymentInfo: {
    monthlyPaymentData: {
      payments: null,
      //uncomment this to test MP bill plan
      // [
      //   {
      //     dueDate: '07/19/2024',
      //     amount: 10.0,
      //     paymentInTransit: '',
      //     isPastDue: false,
      //     creditBackAmount: 0,
      //   },
      //   {
      //     dueDate: '08/19/2024',
      //     amount: 10.0,
      //     paymentInTransit: '',
      //     isPastDue: false,
      //     creditBackAmount: 0,
      //   },
      // ],
    },

    //uncomment this to test AM scheduled payment
    // scheduledPaymentData: { accountLast4: '7777', paymentAmount: '92.00', scheduledDate: '06/20/2024' },
    scheduledPaymentData: 'No active scheduled payment found',
    autopayInfoData: { accountLast4: '1978', cardType: 'VSN', creditCardExpDate: '10/01/2028' },
  },
  notifications: [
    {
      type: MSSNotificationType.WARNING,
      message: "It's time to renew your membership!",
      link: undefined,
    },
    // {
    //   type: MSSNotificationType.ERROR,
    //   message: 'The credit or debit card we have on file to renew your membership has expired',
    //   link: 'https://www.ace.aaa.com',
    // }
  ],
  priceCart: {
    items: [
      {
        id: 'CartItem-ServiceLevel',
        code: CartProductCode.SERVICELEVEL,
        description: 'Classic Membership',
        price: 57,
      },
      {
        id: 'CartItem-Associate',
        code: CartProductCode.ASSOCIATE,
        description: 'Associate Member',
        price: 35,
        discount: 35,
      },
      {
        id: 'CartItem-AutopayDiscount',
        code: CartProductCode.AUTOPAY_DISCOUNT,
        description: 'One-time Auto Pay Discount',
        price: 0,
        discount: 4.5,
      },
    ],
    salesTax: 5,
    savings: 20,
    total: 44,
  },
  prospectiveCosts: {
    classic: {
      memberRoles: {
        DE: 37,
      },
      products: {
        RV: 42,
      },
      discounts: {
        autopay: 4.5,
      },
    },
    plus: {
      memberRoles: {
        DE: 37,
      },
      products: {
        RV: 42,
      },
      discounts: {
        autopay: 4.5,
      },
    },
    premier: {
      memberRoles: {
        DE: 37,
      },
      products: {
        RV: 42,
      },
      discounts: {
        autopay: 4.5,
      },
    },
  },
  additionalDiscounts: [],
  formData: {
    requestedBillPlan: undefined,
    requestedMembershipLevel: undefined,
    members: undefined,
    products: undefined,
  },
  loading: { priceCart: false },
  content: {
    shortBenefits: {
      CLASSIC: [
        'Tow up to 7 miles',
        'Vehicle Lockout/Battery Service',
        'Flat Tire / Fuel Delivery',
        'Select DMV Services in Branches',
        'Approved Auto Repair',
        'Everyday Discounts',
      ],
      PLUS: [
        'Tows up to 100 miles',
        'Vehicle Lockout/Battery Service',
        'Flat Tire / Fuel Delivery',
        'Select DMV Services in Branches',
        'Approved Auto Repair',
        'Everyday Discounts',
      ],
      PREMIER: [
        '1 tow up to 200 miles, remaining tows 100 miles',
        'Vehicle Lockout/Battery Service',
        'Flat Tire / Fuel Delivery',
        'Select DMV Services in Branches',
        'Approved Auto Repair',
        'Everyday Discounts',
      ],
    },
  },
  confirmation: {
    members: [
      {
        roleType: RoleType.PRIMARY,
        firstName: 'GRACE',
        lastName: 'PRIMARY',
        status: MSSMemberStatus.EXISTING,
        customerId: 'some-customer-id',
        emailAddress: 'aaa@aaa.com',
        emailStatusCode: '1',
      },
    ],
    email: 'aaa@aaa.com',
    isEmailValid: true,
    loggedInCustomerId: 'some-customer-id',
    effectiveDate: `09/06/2024`,
    confirmationId: 'some-customer-id0906202404073500A',
    products: [
      {
        status: MSSProductStatus.ADDED,
        type: MembershipProductType.RV,
      },
    ],
    currentBillPlan: CurrentPayPlan.ONETIME,
    updatedBillPlan: CurrentPayPlan.RECURRINGELECTRONICCHECK,
    currentBenefitLevel: MembershipLevel.CLASSIC,
    updatedBenefitLevel: MembershipLevel.CLASSIC,
    amount: 35,
    tags: {},
    paymentDetails: {
      paymentType: CallbackPaymentMethod.Echeck,
      last4: '5678',
      expDate: '',
      cardType: undefined,
      isAutoPayRequested: true,
      autoPaySuccessful: true,
    },
  },
  tags: undefined,
  isMobileApp: false,
};

export const initialState: IState = {
  memberDetails: {
    sessionId: '',
    clubCode: undefined,
    loggedInCustomerId: undefined,
    clubRegion: undefined,
    termEffectiveDate: undefined,
    termExpirationDate: undefined,
    priorTermExpirationDate: undefined,
    joinYear: undefined,
    inRenewal: false,
    inArrears: undefined,
    inGrace: undefined,
    campaignCode: undefined,
    isAutopayRequired: undefined,
    members: undefined,
    benefitLevels: undefined,
    products: undefined,
    enrollments: undefined,
    membershipNumber: undefined,
    loggedInMember: undefined,
    hasPaymentInTransit: false,
    pendingProducts: [],
    prospectiveMemberRules: {
      allowedBillPlans: [],
      allowedMembershipLevels: [],
      allowedGenders: [],
      autopayDiscountAllowed: false,
      downgradesAllowed: false,
      memberProducts: [],
      memberRoles: [],
      upgradesAllowed: true,
    },
    memberCosts: {
      priceCart: {
        items: undefined,
        savings: undefined,
        salesTax: undefined,
        total: 0,
      },
      prospectiveCosts: {
        classic: {},
        plus: {},
        premier: {},
      },
      additionalDiscounts: [],
    },
  },
  paymentInfo: {
    monthlyPaymentData: { payments: null },
    scheduledPaymentData: 'No active scheduled payment found',
    autopayInfoData: null,
  },
  notifications: undefined,
  priceCart: {
    items: undefined,
    savings: undefined,
    salesTax: undefined,
    total: 0,
  },
  prospectiveCosts: {
    classic: {},
    plus: {},
    premier: {},
  },
  additionalDiscounts: [],
  formData: {
    requestedBillPlan: undefined,
    requestedMembershipLevel: undefined,
    members: undefined,
    products: undefined,
  },
  loading: { priceCart: false },
  content: {
    shortBenefits: {
      CLASSIC: [],
      PLUS: [],
      PREMIER: [],
    },
  },
  confirmation: undefined,
  tags: undefined,
  isMobileApp: undefined,
};
